/**
 * Error Handler Utility
 * Centralized error handling for API calls and other operations
 *
 * Usage:
 * try {
 *     await apiClient.get('/endpoint');
 * } catch (error) {
 *     handleApiError(error, 'load data');
 * }
 */

import { AxiosError } from 'axios';
import { notify } from './notify';

/**
 * Object-shaped `detail` or `error` — either a single FastAPI validation
 * error (`{msg}`) or one of our coded errors (`{code, message}`, e.g. the
 * open-house create endpoint's `no_zillow_match` or the rate limiter's
 * `RATE_LIMIT_EXCEEDED`).
 */
interface ApiErrorDetailObject {
    msg?: string;
    code?: string;
    message?: string;
}

interface ApiErrorResponse {
    message?: string;
    /** A plain string, or `{code, message}` from the rate limiter. */
    error?: string | ApiErrorDetailObject;
    detail?: string | ApiErrorDetailObject | Array<{ type?: string; loc?: (string | number)[]; msg?: string; input?: unknown }>;
}

/** Human-readable text of a coded / validation error object. */
const messageFromErrorObject = (err: ApiErrorDetailObject): string =>
    err.message || err.msg || err.code || JSON.stringify(err);

/**
 * The text of an API error's `detail` — a string, a coded `{code, message}`
 * (e.g. a sign-in refused from a blocked country) or a validation list —
 * or `fallback`. Safe to render: never an object.
 */
export function apiErrorDetail(error: unknown, fallback: string): string {
    const detail = (error as { response?: { data?: ApiErrorResponse } })?.response?.data?.detail;
    if (typeof detail === 'string') return detail || fallback;
    if (Array.isArray(detail)) return detail.map((item) => item.msg).filter(Boolean).join(', ') || fallback;
    if (detail && typeof detail === 'object') return messageFromErrorObject(detail);
    return fallback;
}

/** The `detail.code` of a coded API error (`session_revoked`, `country_blocked`…), if any. */
export function apiErrorCode(error: unknown): string | undefined {
    const detail = (error as { response?: { data?: ApiErrorResponse } })?.response?.data?.detail;
    return detail && typeof detail === 'object' && !Array.isArray(detail) ? detail.code : undefined;
}

/**
 * Handle API errors with consistent messaging
 */
export function handleApiError(
    error: unknown,
    action: string,
    options: {
        showToast?: boolean;
        logToConsole?: boolean;
    } = {}
): string {
    const { showToast = true, logToConsole = true } = options;

    let message = `Failed to ${action}`;

    if (error instanceof AxiosError) {
        const data = error.response?.data as ApiErrorResponse;
        const status = error.response?.status;

        // Extract message from response
        if (data?.message) {
            message = data.message;
        } else if (data?.error) {
            message = typeof data.error === 'string' ? data.error : messageFromErrorObject(data.error);
        } else if (data?.detail) {
            // FastAPI validation errors return detail as array of {type, loc, msg, input}
            if (typeof data.detail === 'string') {
                message = data.detail;
            } else if (Array.isArray(data.detail)) {
                // Extract validation error messages
                const errors = data.detail
                    .map((err: { msg?: string; loc?: (string | number)[] }) => {
                        const field = err.loc?.slice(1).join('.') || 'field';
                        return err.msg ? `${field}: ${err.msg}` : String(err);
                    })
                    .join(', ');
                message = errors || `Validation error: ${action}`;
            } else if (typeof data.detail === 'object') {
                message = messageFromErrorObject(data.detail);
            }
        } else {
            // Default messages based on status code
            switch (status) {
                case 400:
                    message = `Invalid request: ${action}`;
                    break;
                case 401:
                    message = 'Your session has expired. Please log in again.';
                    break;
                case 403:
                    message = 'You do not have permission to perform this action';
                    break;
                case 404:
                    message = `The requested resource was not found`;
                    break;
                case 409:
                    message = 'This operation conflicts with existing data';
                    break;
                case 422:
                    message = 'The submitted data is invalid';
                    break;
                case 429:
                    message = 'Too many requests. Please try again later.';
                    break;
                case 500:
                    message = 'Server error. Please try again later.';
                    break;
                case 502:
                case 503:
                case 504:
                    message = 'Service temporarily unavailable. Please try again.';
                    break;
                default:
                    message = `Failed to ${action}`;
            }
        }

        if (logToConsole) {
            console.error(`API Error [${status}] ${action}:`, {
                message,
                url: error.config?.url,
                method: error.config?.method,
                data: error.response?.data,
            });
        }
    } else if (error instanceof Error) {
        if (error.name === 'AbortError') {
            message = 'Request was cancelled';
        } else if (error.message.includes('Network')) {
            message = 'Network error. Please check your connection.';
        } else {
            message = error.message || `Failed to ${action}`;
        }

        if (logToConsole) {
            console.error(`Error ${action}:`, error);
        }
    } else {
        if (logToConsole) {
            console.error(`Unknown error ${action}:`, error);
        }
    }

    if (showToast) {
        notify.error(message);
    }

    return message;
}

/**
 * Create a typed error handler for specific operations
 */
export function createErrorHandler(action: string) {
    return (error: unknown) => handleApiError(error, action);
}

/**
 * Wrapper for async operations with error handling
 */
export async function withErrorHandling<T>(
    operation: () => Promise<T>,
    action: string,
    options?: {
        showToast?: boolean;
        onError?: (error: unknown) => void;
    }
): Promise<T | null> {
    try {
        return await operation();
    } catch (error) {
        handleApiError(error, action, { showToast: options?.showToast ?? true });
        options?.onError?.(error);
        return null;
    }
}

/**
 * Show a success toast. Thin wrapper kept for backwards-compat with
 * existing callsites; new code should use `notify.success` directly
 * from `@/utils/notify` for richer options (description, sticky id).
 */
export function showSuccess(message: string) {
    notify.success(message);
}

/** Show an error toast. */
export function showError(message: string) {
    notify.error(message);
}

/** Show an info toast. */
export function showInfo(message: string) {
    notify.info(message);
}

/** Show a warning toast (amber). */
export function showWarning(message: string) {
    notify.warning(message);
}

export default handleApiError;
